import {
  slab, wallX, wallZ, goal, sign, neon, checkpoint, edgeLights, gravfield, wind,
} from '../kit.js';

/**
 * CHAPTER 2, SECTOR 18: PHOSPHOR. The decaying grid.
 *
 * The fiction made mechanical: this far out the grid is rotting, so
 * portals gutter and die nine seconds after you place them
 * (def.portalDecay, the level rule the DECAY mutator borrows). Every
 * other sector rewards planning ahead. Here, planning ahead is the
 * failure: a portal shot early is dead when you arrive, so the skill is
 * shoot-LATE discipline, placing the far end at the last moment that
 * still leaves time to use it.
 *
 * Three chapters. (1) THE ROT: three bays crossed by wall-to-wall walks,
 * each bay's far wall doubling as the next crossing's entry, so you watch
 * the portal behind you gutter and die while you line up the next one.
 * (2) THE RELAY: step into a low-gravity void with wind at your back and
 * shoot the pair MID-FALL, floor below and far wall ahead; the slow fall
 * is the aiming budget, and a far-wall portal placed from the safety of
 * the launch deck has rotted before you get there. (3) THE LONG DARK: a
 * chain of islands over void where you own exactly one pair, so each
 * crossing spends the portals the last one used.
 *
 * Rot arithmetic: 9s of life, the last third of which visibly gutters
 * (PortalSystem shrinks the iris below 30% remaining), so the warning is
 * about three seconds long and expiry is never a surprise.
 */
/**
 * One island of the long dark. The far wall is SPLIT, and that is the
 * whole trick of the chapter: the doorway is the sight line to the next
 * island's wall, and the two segments flanking it are what you walk into.
 * One wall doing both jobs is why a single pair can carry the chain.
 *
 * A first cut gave each island a near-side wall as well. It blocked the
 * view of its own far face (measured: every shot down the chain landed on
 * it), and a portal on it would have opened over the gap.
 */
const island = (x, z, y = 0, w = 12) => [
  slab(x, y, z, w, w, 'plate', 1.2),
  ...edgeLights(x, y, z, w, w, 'lime'),
  wallZ(x - 4.5, y, z - w / 2 - 0.7, 5, 11, 'panel', 1.2),
  wallZ(x + 4.5, y, z - w / 2 - 0.7, 5, 11, 'panel', 1.2),
];

export default {
  id: 'phosphor',
  name: 'PHOSPHOR',
  blurb: 'Nothing holds. Shoot late.',
  mode: { type: 'sprint', par: 200 },
  // REDLINE: six seconds of rot instead of nine; the clock is the
  // shoot-late discipline made mandatory.
  redline: { seconds: 180, portalDecay: 6 },
  portalDecay: 9,
  spawn: { p: [0, 0.4, 10], yaw: 0 },
  killY: -52,
  env: {
    fog: 0x0c1410,
    fogDensity: 0.0044,
    sky: { zenith: 0x02070a, horizon: 0x0a1a16, glow: 0x1a8a5a, aurora: 0x6cffc0 },
    floorY: -300,
  },
  objects: [
    // ---- chapter 1: the rot. Three bays, two crossings, and a portal
    // dying behind you each time. Every far wall is the next crossing's
    // entry, so the pair is always being spent and re-spent.
    slab(0, 0, 6, 18, 20, 'plate', 1.4),
    ...edgeLights(0, 0, 6, 18, 20, 'lime'),
    wallZ(0, 0, 16.7, 20, 10, 'panel', 1.4),
    wallX(-9.7, 0, 6, 20, 10, 'panel', 1.4),
    wallX(9.7, 0, 6, 20, 10, 'plate', 1.4),
    neon(0, 9.4, 16.0, [18, 0.14, 0.14], 'lime'),

    slab(0, 0, -36, 18, 20, 'plate', 1.4),
    ...edgeLights(0, 0, -36, 18, 20, 'lime'),
    wallZ(0, 0, -46.7, 20, 11, 'panel', 1.4),
    wallX(-9.7, 0, -36, 20, 10, 'plate', 1.4),
    wallX(9.7, 0, -36, 20, 10, 'plate', 1.4),
    neon(0, 10.4, -46.0, [18, 0.14, 0.14], 'lime'),
    checkpoint(0, 0, -32),

    // The launch deck ends chapter one and starts chapter two: its far
    // wall is split, so the doorway shows the void before you step at it.
    slab(0, 0, -78, 18, 20, 'plate', 1.4),
    ...edgeLights(0, 0, -78, 18, 20, 'lime'),
    wallZ(-7, 0, -88.7, 8, 11, 'panel', 1.4),
    wallZ(7, 0, -88.7, 8, 11, 'panel', 1.4),
    wallX(-9.7, 0, -78, 20, 10, 'plate', 1.4),
    wallX(9.7, 0, -78, 20, 10, 'plate', 1.4),
    checkpoint(0, 0, -74),

    sign(0, 3.6, 2, 0, [
      'PHOSPHOR',
      'PORTALS ROT IN NINE SECONDS',
      'SHOOT LATE, NOT EARLY',
    ]),
    // x -4, not -7: a 7.3m single-line sign centred at -7 ran 2m into the
    // west wall (inner face at -9) and read as '...CH IT DIE BEHIND YOU'.
    sign(-4, 2.6, -30, 0, ['WATCH IT DIE BEHIND YOU'], '#6cffc0'),
    sign(7, 2.6, -72, 0, [
      'COURIER LOG 05',
      'NOTHING HOLDS',
      'CARRY WHAT YOU NEED IN YOUR HANDS',
    ], '#6cffc0'),

    // ---- chapter 2: the relay. Step into a low-gravity shaft with a
    // tailwind: the slow fall IS the aiming budget (measured ~3s from the
    // lip to the catch floor). The move is DESCENT's, under a rot clock:
    // tag the launch wall BEHIND you up high, tag the catch floor below,
    // fall into the floor and fly out of the wall toward the far deck.
    // A wall tagged from the safety of the deck has rotted before the
    // fall delivers you to it, which is the whole lesson.
    //
    // Arithmetic: 34m of fall at 0.3g (7.8 m/s^2) arrives at ~23 m/s and
    // takes ~3.0s. Exiting at y 8, the 30m drop to the far deck buys
    // ~2.8s, which carries ~64m against a 50m gap. The wind starts PAST
    // the shaft on purpose: blowing on the fall itself carried the body
    // clean past the floor portal it had just placed.
    gravfield(0, -12, -115, 34, 56, 58, 0.3),
    wind(0, -6, -136, 34, 44, 52, [0, 0, -1], 16, 24),
    slab(0, -34, -116, 30, 58, 'panel', 1.5),
    neon(0, -33.4, -116, [26, 0.16, 0.16], 'lime'),
    wallZ(0, -34, -145.7, 32, 22, 'panel', 1.5),
    wallX(-15.7, -34, -116, 58, 22, 'plate', 1.5),
    wallX(15.7, -34, -116, 58, 22, 'plate', 1.5),

    slab(0, -22, -150, 20, 24, 'plate', 1.4),
    ...edgeLights(0, -22, -150, 20, 24, 'lime'),
    wallZ(-7, -22, -162.7, 8, 12, 'panel', 1.4),
    wallZ(7, -22, -162.7, 8, 12, 'panel', 1.4),
    neon(0, -10.6, -162.0, [20, 0.14, 0.14], 'lime'),
    checkpoint(0, -22, -144),
    sign(0, -18.4, -140, 180, ['THE RELAY', 'TAG THE WALL BEHIND YOU'], '#6cffc0'),

    // ---- chapter 3: the long dark. One pair, three crossings. Each
    // island's entry face is the wall you walk into; the next island's
    // exit face is the one you tag across the gap. Nothing here survives
    // long enough to be pre-placed.
    ...island(0, -190, -22),
    ...island(0, -226, -22),
    // The goal deck follows the island rule: its portalable panels flank
    // the FAR edge, so the third crossing exits onto the deck. The first
    // cut put them on the near edge, and a portal there opened 0.6m past
    // the deck's lip facing the void: traced, the body left the portal
    // airborne at z -252.8 heading away from the deck and fell. The middle
    // segment stays solid plate so the goal has a back wall.
    slab(0, -22, -262, 16, 16, 'plate', 1.2),
    ...edgeLights(0, -22, -262, 16, 16, 'lime'),
    wallZ(-6.5, -22, -270.7, 5, 11, 'panel', 1.2),
    wallZ(6.5, -22, -270.7, 5, 11, 'panel', 1.2),
    wallZ(0, -22, -270.7, 8, 11, 'plate', 1.2),
    goal(0, -22, -266, 0),
    checkpoint(0, -22, -258),
    sign(0, -18.6, -186, 180, ['THE LONG DARK', 'ONE PAIR, THREE GAPS'], '#6cffc0'),
  ],
};
